import { useQuery } from '@tanstack/react-query';
import { rawClient } from '@/lib/api/client';
import { ApiException, CLIENT_ERROR_CODES, type ApiResponse } from '@/lib/api/types';
import { REFRESH_INTERVAL } from '@/lib/constants';
import type { HealthResponse } from './types';

/**
 * 전 구간 연결 상태를 조회한다.
 *
 * 왜 api.get 을 쓰지 않는가
 *   백엔드는 구성요소가 죽으면 HTTP 503을 준다.
 *   200으로 주고 본문에만 DOWN을 담으면 모니터링 도구가 정상으로 오인하기 때문이다.
 *   그런데 axios는 503을 에러로 처리하므로 공용 api.get 을 쓰면
 *   "서버 오류"만 뜨고 무엇이 죽었는지 알 수 없다.
 *
 *   그래서 이 훅만 rawClient를 쓰고 validateStatus로 503을 정상 흐름에 포함시킨다.
 *   503은 "조회에 성공했고 그 결과가 DOWN"이라는 뜻이다.
 *   진짜 조회 실패(500, 네트워크 단절)와는 구분되어야 한다.
 */
async function fetchHealth(): Promise<HealthResponse> {
  const response = await rawClient.get<ApiResponse<HealthResponse>>('/health', {
    // 200과 503만 정상 흐름으로 본다. 나머지는 axios가 에러로 던진다.
    validateStatus: (status) => status === 200 || status === 503,
  });

  const body = response.data;
  if (!body.success || body.data === null) {
    throw new ApiException(
      body.error?.code ?? CLIENT_ERROR_CODES.UNKNOWN,
      body.error?.message ?? '헬스체크 응답을 해석할 수 없습니다.',
      response.status,
    );
  }
  return body.data;
}

export function useHealth() {
  return useQuery<HealthResponse>({
    queryKey: ['health'],
    queryFn: fetchHealth,

    // 헬스 화면은 최신 상태가 중요하므로 주기적으로 다시 묻는다.
    refetchInterval: REFRESH_INTERVAL.HEALTH,
    // 화면을 다시 볼 때도 갱신한다. 전역 기본값(false)을 여기서만 뒤집는다.
    refetchOnWindowFocus: true,
    staleTime: 0,
    // 장애 상황을 빨리 보여주는 것이 중요하므로 재시도를 줄인다.
    retry: 1,
  });
}
